import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../layouts';
import AccountSettings from '../components/AccountSettings';
import { useAuth } from '../contexts/AuthContext';
import { useMetaTags } from '../hooks/useMetaTags';
import { DebugLabel } from '../components/shared';
import { TrashIcon } from '@heroicons/react/24/outline';

export const ProfilePage: React.FC = () => {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [deleteLoading, setDeleteLoading] = useState(false);
  useMetaTags({
    title: 'Profile | Smart Prompt',
    url: 'https://www.smartprompt.us/profile'
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true });
    }
  }, [loading, navigate, user]);

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (!user) return;

    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action is IRREVERSIBLE and will delete:\n\n' +
      '• Your profile information\n' +
      '• All your comparisons\n' +
      '• All your saved data\n\n' +
      'Type "DELETE" in the next prompt to confirm.'
    );

    if (!confirmed) return;

    const verification = window.prompt('Type "DELETE" to confirm account deletion:');

    if (verification !== 'DELETE') {
      alert('Account deletion cancelled. Verification text did not match.');
      return;
    }

    try {
      setDeleteLoading(true);
      const token = await user.getIdToken();

      const response = await fetch('/api/auth/delete-account', {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle deletion failure
        if (data.error === 'Account deletion failed' || data.error === 'Partial deletion failure') {
          const isPartialDeletion = data.details?.databaseDeleted === true;

          if (isPartialDeletion) {
            // Old orphaned accounts - database deleted but Firebase failed
            alert(
              '⚠️ PARTIAL DELETION WARNING\n\n' +
              'Your database records have been deleted, but your Firebase Authentication account could not be removed.\n\n' +
              'This is likely a permission issue on our server. Please contact support immediately with this information:\n\n' +
              `• Error Code: ${data.details?.errorCode || 'unknown'}\n` +
              `• Database Deleted: ${data.details?.databaseDeleted ? 'Yes' : 'No'}\n` +
              `• Firebase Deleted: ${data.details?.firebaseDeleted ? 'Yes' : 'No'}\n\n` +
              'Support Email: support@smartprompt.com'
            );

            // Sign out anyway since database records are deleted
            await signOut();
            navigate('/');
            return;
          } else {
            // New behavior - account intact, Firebase deletion failed before DB deletion
            alert(
              '❌ ACCOUNT DELETION FAILED\n\n' +
              'We were unable to delete your account due to a server configuration issue.\n\n' +
              'Your account is still intact and has not been deleted.\n\n' +
              `Error: ${data.details?.errorCode || 'unknown'}\n\n` +
              'Please contact support:\n' +
              'Support Email: support@smartprompt.com'
            );
            return;
          }
        }

        throw new Error(data.message || 'Deletion failed');
      }

      // Success - check if there were any warnings
      if (data.details?.firebaseDeleted === false) {
        console.warn('Account deleted but Firebase cleanup may be incomplete:', data);
      }

      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Deletion failed:', err);
      alert(
        'Failed to delete account.\n\n' +
        (err instanceof Error ? err.message : 'Unknown error occurred') +
        '\n\nPlease try again or contact support if the problem persists.'
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto px-6 py-8">
        <DebugLabel label="ProfilePage-Container" type="page" position="top-left" />

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Profile</h1>
          <p className="text-slate-400">Manage your profile</p>
        </div>

        {loading ? (
          <div className="text-slate-300">Loading profile...</div>
        ) : user ? (
          <>
            <AccountSettings />

            {/* Danger Zone - Delete Account */}
            <div className="mt-8 bg-red-500/5 rounded-lg border border-red-500/20 overflow-hidden">
              <div className="p-6 border-b border-red-500/20">
                <h2 className="text-xl font-semibold text-red-400 flex items-center gap-2">
                  <TrashIcon className="w-5 h-5" />
                  Danger Zone
                </h2>
                <p className="text-sm text-slate-400 mt-1">Irreversible actions</p>
              </div>

              <div className="p-6">
                <h3 className="text-white font-medium mb-2">Delete Account</h3>
                <p className="text-sm text-slate-400 mb-4">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-lg transition-colors"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-slate-300">Redirecting to login...</div>
        )}
      </div>
    </MainLayout>
  );
};

export default ProfilePage;
